import { AbstractTask, TaskStatus, TaskType, CODE_UNKNOWN } from "./task.ts";
import { downloadFile } from "../../utils/file.ts";
import * as Mp4Muxer from "mp4-muxer";

export class EncodeVideoTaskWebCodecs extends AbstractTask {
    #imgs: File[];
    #q: number;
    #fps: number;
    #hl: number;
    #outputFileData: Blob | null = null;
    constructor(fileObjectList: File[], quality = 31, fps = 25, heightLimit = -1) {
        super(TaskType.EncodeVideo, fileObjectList.length, "");
        this.#imgs = [...fileObjectList];
        this.#fps = fps;
        this.#q = quality; 
        this.#hl = heightLimit;
    }

    override _action = async (abortSignal: AbortSignal) => {
        if (this.#imgs.length === 0) return;

        // --- Setup Encoder ---
        const firstBitmap = await createImageBitmap(this.#imgs[0]);
        const videoWidth = this.#hl > 0 ? Math.round(firstBitmap.width * (this.#hl / firstBitmap.height)) : firstBitmap.width;
        const videoHeight = this.#hl > 0 ? this.#hl : firstBitmap.height;
        // Dimensions must be even for most codecs
        const w = videoWidth % 2 === 0 ? videoWidth : videoWidth - 1;
        const h = videoHeight % 2 === 0 ? videoHeight : videoHeight - 1;
        firstBitmap.close();

        // Initialize Muxer
        const muxer = new Mp4Muxer.Muxer({
            target: new Mp4Muxer.ArrayBufferTarget(),
            video: {
                codec: "avc",
                width: w,
                height: h
            },
            fastStart: "in-memory", // Move moov atom to start for better streaming/compatibility
        });

        // Initialize VideoEncoder
        const encoder = new VideoEncoder({
            output: (chunk, meta) => {
                try {
                    muxer.addVideoChunk(chunk, meta);
                } catch (e) {
                    console.error("Muxer error", e);
                    this.$store.setKey("info", `Muxer Error: ${e}`);
                    this.$store.setKey("errorCode", CODE_UNKNOWN);
                    this.$store.setKey("status", TaskStatus.Error);
                }
            },
            error: (e) => {
                console.error("VideoEncoder error", e);
                this.$store.setKey("info", `Encoding Error: ${e.message || e}`);
                this.$store.setKey("errorCode", CODE_UNKNOWN);
                this.$store.setKey("status", TaskStatus.Error);
            },
        });

        // Calculate bitrate based on quality slider (15-50)
        // q=15 (Best) -> 50 Mbps (Extreme Quality)
        // q=23 (Default) -> 25 Mbps (High Quality)
        // q=50 (Worst) -> 4 Mbps
        // Formula: Map [15, 50] to [50M, 4M]
        // Slope = (4 - 50) / (50 - 15) = -46 / 35 ≈ -1.31
        // Mbps = 50 - (q - 15) * 1.31
        const bitrateMbps = Math.max(4, 50 - (this.#q - 15) * 1.31);
        
        const config: VideoEncoderConfig = {
            codec: "avc1.42001E", // Baseline profile, level 3.0 (Default)
            width: w,
            height: h,
            bitrate: Math.floor(bitrateMbps * 1_000_000), 
            framerate: this.#fps,
            avc: { format: "avc" }, 
        };

        try {
            // Check Profile Support (High -> Main -> Baseline)
            // Safari often prefers High Profile ("avc1.64001E")
            const profiles = [
                "avc1.64001E", // High Profile (Best Quality, Safari preferred)
                "avc1.4D001E", // Main Profile
                "avc1.42001E", // Baseline Profile (Most compatible)
            ];

            let supported = false;
            for (const codec of profiles) {
                config.codec = codec;
                const support = await VideoEncoder.isConfigSupported(config);
                if (support.supported) {
                    console.log(`WebCodecs: Selected supported codec ${codec}`);
                    supported = true;
                    break;
                }
            }

            if (!supported) {
                throw new Error("H.264 hardware encoding not supported by your browser.");
            }

            encoder.configure(config);

            // Process images
            let frameIdx = 0;
            
            // Canvas for resizing
            const offscreen = new OffscreenCanvas(w, h);
            const ctx = offscreen.getContext("2d");
            if (!ctx) throw new Error("Canvas context failed");

            // Fill background black once
            ctx.fillStyle = "black";
            ctx.fillRect(0, 0, w, h);

            for (const imgFile of this.#imgs) {
                if (this.$store.get().status === TaskStatus.Error) break; // Stop if error occurred in callback
                abortSignal.throwIfAborted();
                const bitmap = await createImageBitmap(imgFile);
                
                // Draw to canvas to resize
                ctx.drawImage(bitmap, 0, 0, w, h);
                bitmap.close();

                const frame = new VideoFrame(offscreen, {
                    timestamp: (frameIdx * 1000 * 1000) / this.#fps, // microseconds
                    duration: (1000 * 1000) / this.#fps,
                });

                // Encode
                // Throttle encoding to avoid memory pressure
                if (encoder.encodeQueueSize > 5) {
                    await encoder.flush();
                }
                
                // Keyframe every 2s
                encoder.encode(frame, { keyFrame: frameIdx % (this.#fps * 2) === 0 });
                frame.close();
                
                frameIdx++;
                this.$store.setKey("current", frameIdx);
            }

            await encoder.flush();
            encoder.close();

            // Finalize MP4
            muxer.finalize();
            
            const { buffer } = muxer.target;
            this.#outputFileData = new Blob([buffer], { type: "video/mp4" });
        } catch (e) {
            console.error("Task execution error", e);
            // If it's not already reported
            if (this.$store.get().status !== TaskStatus.Error) {
                 this.$store.setKey("info", `Error: ${(e as Error).message}`);
                 this.$store.setKey("errorCode", CODE_UNKNOWN);
                 // status will be set to Error by the caller (AbstractTask.start)
                 throw e; 
            }
        }
    };

    downloadOutputFile() {
        if (this.#outputFileData) {
            downloadFile([this.#outputFileData], "output.mp4");
        }
    }
    getOutputBlob() {
        return this.#outputFileData || new Blob([], { type: "video/mp4" });
    }
}
